import { forwardRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { Avatar } from '@/components/avatar'
import Icon from '@/components/icon'
import { createModal, useModalize } from '@/components/modalize'
import VerifiedBadge from '@/components/verified-badge'
import UnverifiedBadge from '@/components/unverified-badge'
import useMediaQuery from '@/hooks/use-media-query'
import { logUserView } from '@/ports'
import { breakpoints, colors, fontSizes, fontWeights } from '@/styles'
import formatName from '@/utils/format-name'
import { getImageUrl } from '@/utils/image'

const Wrapper = styled.div`
  background: ${colors.white};
  position: relative;
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  height: 100%;
  max-height: 98vh;
  max-width: 767px;

  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    max-height: unset;
    margin-top: 68px;
    border-radius: 16px 16px 0 0;
  }
`

const ModalClose = styled.div`
  position: absolute;
  top: 19px;
  right: 25px;
  z-index: 1;

  svg {
    cursor: pointer;
    margin: 0;
  }

  :hover {
    opacity: 0.7;
  }

  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    top: 20px;
    right: 20px;
  }
`

const ScrollWrapper = styled.div`
  background: ${colors.white};
  height: 100%;
  overflow-y: scroll;
  display: flex;
  justify-content: space-between;
  flex-shrink: 0;
  padding: 0px 24px 35px;

  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    height: calc(100% - 160px);
    padding: 0px 24px 0px;
    flex-direction: column;
    align-items: center;
  }
`

const ProfileWrapper = styled.div`
  display: flex;
  flex-direction: column;
  border: 1px solid ${colors.grey};
  width: 282px;
  border-radius: 6px;
  height: 100%;

  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    height: unset;
    margin-bottom: 0;
    width: 100%;
    max-width: 460px;
  }
`

const AvatarWrapper = styled.div`
  height: 280px;
  width: 280px;
  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    width: 100%;
    div > img {
      object-position: top;
    }
  }
`

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  padding: 16px;

  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    padding: 20px 16px 10px;
    overflow: hidden;
  }
`

const NameWrapper = styled.div`
  display: flex;
  align-items: center;
`

const Name = styled.h2`
  font-size: 25px;
  color: ${colors.regular};
  font-weight: ${fontWeights.bold};
  letter-spacing: 0.47px;
  line-height: 34px;
  padding: 0;
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const AboutWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  margin-left: 24px;

  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    margin-left: 20px;
    width: 100%;
    max-width: 460px;
    margin: 20px 0 40px;
  }
`

const SectionTitle = styled.h3`
  font-size: ${fontSizes.medium};
  font-weight: ${fontWeights.medium};

  :not(:first-child) {
    margin-top: 20px;
  }
`

const ProfileTitle = styled.h3`
  font-size: ${fontSizes.medium};
  font-weight: ${fontWeights.bold};
  border-bottom: 1px solid ${colors.grey};
  margin: 10px 0 20px;
  padding-bottom: 20px;
  padding-left: 24px;

  :not(:first-child) {
    margin-top: 28px;
  }
`

const Row = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  :not(:last-child) {
    border-bottom: 1px solid ${colors.grey};
  }
`

const Label = styled.p`
  width: 190px;

  display: flex;
  align-items: center;
  color: ${colors.mediumGrey};
  overflow-wrap: break-word;
  word-break: break-word;
  flex-shrink: 0;
  margin: 15px 0;
  margin-right: 15px;

  svg {
    margin-right: 10px;
    opacity: 0.8;
  }

  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    width: 160px;
  }
`

const Value = styled(Label)`
  flex-direction: column;
  align-items: flex-start;
  text-align: left;
  color: ${colors.regular};
  font-weight: ${fontWeights.medium};
  margin: 15px 0 16px;
  flex-shrink: 1;
`

const Story = styled(Value)`
  width: 392px;
  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    width: 100%;
  }
`

const InfoContainer = styled.div`
  margin-top: 20px;
`

const UnverifiedWrapper = styled.div`
  margin-left: 5px;
`

const CompanyName = styled.span`
  font-size: 14px;
  color: ${colors.grey48};
`

const LandlordProfile = forwardRef(({ data }, ref) => {
  const { close } = useModalize()
  const { t } = useTranslation()
  const displayMobile = useMediaQuery(`(max-width: ${breakpoints.ipadMiniMax})`)
  const landlordName = formatName(data)
  // BE uses preference and preferences here interchangeably.  Check both.
  const isVerified =
    data.identity_verified_state === 'verified' || data.identity_verified_state === true

  useEffect(() => {
    logUserView(undefined, { userId: data.id })
  }, [data])

  return (
    <Wrapper ref={ref}>
      <ModalClose>
        <Icon aria-label="Close" id="close" width={24} height={24} onClick={close} />
      </ModalClose>
      <ProfileTitle>{t('r.profile.view.profile.subtitle', 'Profile')}</ProfileTitle>
      <ScrollWrapper>
        <ProfileWrapper>
          <AvatarWrapper>
            <Avatar
              shadow={false}
              absolute={false}
              location={getImageUrl(data.avatar?.aws_s3_key, {
                width: 280,
                height: 280
              })}
              height={280}
              width={displayMobile ? '100%' : 280}
              radius={'6px 6px 0 0'}
            />
          </AvatarWrapper>
          <ContentWrapper>
            <NameWrapper>
              <Name>{landlordName}</Name>
              {isVerified ? (
                <VerifiedBadge
                  type="id"
                  width={25}
                  height={25}
                  bottom={-3}
                  verified={data.identity_verified_state}
                />
              ) : (
                <UnverifiedWrapper>
                  <UnverifiedBadge />
                </UnverifiedWrapper>
              )}
            </NameWrapper>
            {data.company_name && (
              <InfoContainer>
                <CompanyName>{data.company_name}</CompanyName>
              </InfoContainer>
            )}
          </ContentWrapper>
        </ProfileWrapper>
        <AboutWrapper>
          <SectionTitle>{t('r.profile.view.story.subtitle', 'Story')}</SectionTitle>
          <Row>
            <Story>{data.about_me ? data.about_me : '-'}</Story>
          </Row>
          {data?.company_name && (
            <>
              <SectionTitle>
                {t('r.profile.view.company_info.subtitle', 'Company Info')}
              </SectionTitle>
              <Row>
                <Label>{t('r.profile.view.company_name.label', 'Company Name')}</Label>
                <Value>{data.company_name ? data.company_name : '-'}</Value>
              </Row>
              <Row>
                <Label>{t('r.profile.view.address.label', 'Address')}</Label>
                <Value>{data.address ? data.address : '-'}</Value>
              </Row>
              <Row>
                <Label>{t('r.profile.view.brokerage.label', 'Brokerage')}</Label>
                <Value>{data.brokerage ? data.brokerage : '-'}</Value>
              </Row>
              <Row>
                <Label>{t('r.profile.view.business_license.label', 'Business License')}</Label>
                <Value>{data.business_license ? data.business_license : '-'}</Value>
              </Row>

              <SectionTitle>
                {t('r.profile.view.work_history.subtitle', 'Work History')}
              </SectionTitle>
              <Row>
                <Label>{t('r.profile.view.year_started.label', 'Year Started')}</Label>
                <Value>{data.year_started ? data.year_started : '-'}</Value>
              </Row>
              <Row>
                <Label>{t('r.profile.view.properties_managed.label', 'Properties Managed')}</Label>
                <Value>{data.properties_managed ? data.properties_managed : '-'}</Value>
              </Row>
            </>
          )}
        </AboutWrapper>
      </ScrollWrapper>
    </Wrapper>
  )
})

LandlordProfile.displayName = 'LandlordProfile'

export default LandlordProfile

export const showLandlordProfile = createModal(LandlordProfile, {
  width: 'auto',
  maxWidth: '767px',
  tabletFullscreen: true,
  alignSelf: 'flex-start',
  showBackground: false,
  disableOverflowMobile: true
})
